import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { type Auth, connectAuthEmulator, getAuth } from 'firebase/auth';

// ============================================================
// Lume / TEACare PWA — bootstrap Firebase (Camada 1: app + auth)
// ============================================================
// M3.3: este módulo é o ÚNICO ponto que pode ser importado
// estaticamente pelo entry-chunk (main.tsx, AuthContext, App).
//
// Importa apenas `firebase/app` e `firebase/auth`. Firestore,
// Functions, Storage, Messaging vivem em módulos siblings:
//   - ./firestore-init  (db)
//   - ./functions-init  (fns)
//   - ./storage-init    (storage)
//   - ./messaging-init  (initMessaging)
//
// Esses módulos só entram no bundle quando algum chunk lazy os
// importa — o que acontece apenas dentro de rotas /app/*.
//
// Camadas:
//   Camada 1 (síncrona, eager):  firebase/app, firebase/auth
//   Camada 2 (lazy via /app/*):  firestore, functions, storage
//   Camada 3 (opcional):         messaging (com isSupported)
// ============================================================

export const FUNCTIONS_REGION = import.meta.env.VITE_FUNCTIONS_REGION ?? 'southamerica-east1';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let appSingleton: FirebaseApp | null = null;
let authEmulatorConnected = false;

export function shouldUseEmulator(): boolean {
  return import.meta.env.VITE_USE_EMULATOR === 'true' && typeof window !== 'undefined';
}

/**
 * Inicializa o app Firebase + Auth — Camada 1.
 *
 * Chamado uma única vez por `main.tsx`. NÃO carrega Firestore, Functions,
 * Storage ou Messaging — estes chegam via os chunks das rotas autenticadas.
 *
 * Idempotente: chamadas subsequentes devolvem o mesmo singleton.
 */
export function initFirebaseAuth(): FirebaseApp {
  if (appSingleton) return appSingleton;

  const [first] = getApps();
  appSingleton = first ?? initializeApp(firebaseConfig);

  if (shouldUseEmulator() && !authEmulatorConnected) {
    connectAuthEmulator(getAuth(appSingleton), 'http://localhost:9099', {
      disableWarnings: true,
    });
    authEmulatorConnected = true;
  }

  return appSingleton;
}

/**
 * Alias retrocompatível. Em código novo prefira `initFirebaseAuth()`.
 */
export function initFirebase(): FirebaseApp {
  return initFirebaseAuth();
}

/**
 * Devolve o app singleton, inicializando-o em demanda. Usado pelos
 * acessores das camadas 2 e 3.
 */
export function ensureApp(): FirebaseApp {
  return appSingleton ?? initFirebaseAuth();
}

export function authClient(): Auth {
  return getAuth(ensureApp());
}
